/*
 * Lanzamiento programado.
 *
 * Liquid ya deja renderizado el estado bloqueado: los controles llegan con
 * disabled/aria-disabled y la compra dinámica con hidden/inert. Este script
 * solo tiene que mantener la cuenta regresiva, liberar los controles al
 * vencer y volver a aplicar el bloqueo cuando otro componente del theme
 * reescribe el formulario.
 *
 * Reglas:
 * - No hay reloj si no hay lanzamientos activos.
 * - La coordinación con las variantes usa el bus del theme
 *   (`<productId>__VARIANT_CHANGE`), no la observación de atributos.
 * - El único observador mira childList sobre el body para descubrir marcado
 *   insertado después del arranque (quick view, rejillas de tarjetas) y las
 *   regiones de carrito que Section Rendering reemplaza. Nunca observa
 *   atributos y solo procesa los nodos añadidos.
 * - Solo se retiran marcas aplicadas o adoptadas por este script; los bloqueos
 *   ajenos (agotado, términos y condiciones) se respetan.
 */
(() => {
  const SECOND = 1000;

  const SELECTORS = {
    timer: 'scheduled-launch[data-scheduled-launch-at]',
    form: '[data-scheduled-launch-form][data-scheduled-launch-at]',
    control: '[data-launch-control]',
    dynamicCheckout: '[data-launch-dynamic-checkout]',
    checkout: '[data-scheduled-launch-checkout]',
    acceleratedCheckout: '[data-scheduled-launch-accelerated]',
    cartTimer: 'scheduled-launch[data-launch-role="cart"][data-scheduled-launch-at]',
    cartItems: '[data-minimog-cart-items]',
    launchMarkup: 'scheduled-launch[data-scheduled-launch-at], [data-scheduled-launch-form][data-scheduled-launch-at]',
    bundle: '.product-bundles',
    bundleButton: '[data-add-bundle]',
    purchaseControl: '[name="add"], .shopify-payment-button__button'
  };

  const targetTime = (element) => Number(element.dataset.scheduledLaunchAt || 0) * SECOND;
  const isActive = (element, now = Date.now()) => targetTime(element) > now;
  const pad = (value) => String(value).padStart(2, '0');
  const strings = () => window.scheduledLaunchStrings || {};

  /*
   * Marcas aplicadas o adoptadas por este script, para no retirar bloqueos
   * ajenos. Liquid ya renderiza el estado bloqueado, así que al registrar un
   * lanzamiento hay que adoptar lo que ya viene puesto: de lo contrario nunca
   * podría retirarse al vencer. Solo se reclama lo que Liquid emite dentro de
   * la rama del lanzamiento, nunca el disabled de términos y condiciones.
   */
  const owned = new WeakMap();

  const marks = (element) => {
    if (!owned.has(element)) owned.set(element, new Set());
    return owned.get(element);
  };

  const claim = (element, attribute, value) => {
    if (!element.hasAttribute(attribute)) element.setAttribute(attribute, value);
    marks(element).add(attribute);
  };

  const claimClass = (element, name) => {
    element.classList.add(name);
    marks(element).add(`class:${name}`);
  };

  const release = (element, attribute) => {
    const claimed = owned.get(element);
    if (!claimed || !claimed.has(attribute)) return;
    element.removeAttribute(attribute);
    claimed.delete(attribute);
  };

  const releaseClass = (element, name) => {
    const claimed = owned.get(element);
    if (!claimed || !claimed.has(`class:${name}`)) return;
    element.classList.remove(name);
    claimed.delete(`class:${name}`);
  };

  const releaseAll = (element, attributes) => attributes.forEach((name) => release(element, name));

  class ScheduledLaunch {
    constructor() {
      this.timers = new Set();
      this.forms = new Set();
      this.variantSubscriptions = new Map();
      this.interval = null;
      this.announcedMinute = new WeakMap();

      this.tick = this.tick.bind(this);
      this.onResize = this.onResize.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
      this.handleClick = this.handleClick.bind(this);
      this.handleTermsChange = this.handleTermsChange.bind(this);

      document.addEventListener('submit', this.handleSubmit, true);
      document.addEventListener('click', this.handleClick, true);
      document.addEventListener('change', this.handleTermsChange);

      this.needsScan = false;
      this.queued = false;

      this.scan(document);
      this.watchDom();
      this.subscribeCartUpdates();
    }

    /* ---------------------------------------------------------------- scan */

    scan(root) {
      const scope = root && root.querySelectorAll ? root : document;

      scope.querySelectorAll(SELECTORS.timer).forEach((timer) => {
        if (this.timers.has(timer)) return;
        this.timers.add(timer);
        if (!isActive(timer)) return this.releaseTimer(timer);
        this.syncWidth(timer);
        this.updateTimer(timer, Date.now());
      });

      scope.querySelectorAll(SELECTORS.form).forEach((form) => {
        if (this.forms.has(form)) return;
        this.forms.add(form);
        if (isActive(form)) this.lockForm(form);
        this.watchVariants(form);
      });

      this.syncCheckout();
      this.syncBundles();
      this.ensureClock();
    }

    forget(element, collection) {
      collection.delete(element);
    }

    /* --------------------------------------------------------------- clock */

    ensureClock() {
      const now = Date.now();
      const pending = (element) => element.isConnected && isActive(element, now);
      const running = [...this.timers].some(pending) || [...this.forms].some(pending);

      if (running && this.interval === null) {
        this.interval = window.setInterval(this.tick, SECOND);
        window.addEventListener('resize', this.onResize);
      } else if (!running && this.interval !== null) {
        window.clearInterval(this.interval);
        window.removeEventListener('resize', this.onResize);
        this.interval = null;
      }
    }

    tick() {
      const now = Date.now();

      this.timers.forEach((timer) => {
        if (!timer.isConnected) return this.forget(timer, this.timers);
        if (isActive(timer, now)) this.updateTimer(timer, now);
        else this.releaseTimer(timer);
      });

      this.forms.forEach((form) => {
        if (!form.isConnected) {
          this.unwatchVariants(form);
          return this.forget(form, this.forms);
        }
        if (isActive(form, now)) this.lockForm(form);
        else this.releaseForm(form);
      });

      this.syncCheckout();
      this.syncBundles();
      this.ensureClock();
    }

    onResize() {
      this.timers.forEach((timer) => this.syncWidth(timer));
    }

    /* -------------------------------------------------------------- cuenta */

    updateTimer(timer, now) {
      const remaining = Math.max(0, targetTime(timer) - now);
      const totalSeconds = Math.floor(remaining / SECOND);
      const days = Math.floor(totalSeconds / 86400);
      const hours = Math.floor((totalSeconds % 86400) / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      Object.entries({ days, hours, minutes, seconds }).forEach(([unit, value]) => {
        const node = timer.querySelector(`[data-launch-${unit}]`);
        if (node) node.textContent = pad(value);
      });

      this.announce(timer, { days, hours, minutes });
    }

    /*
     * La lectura accesible se actualiza como mucho una vez por minuto: los
     * segundos quedan en los dígitos visuales, que son aria-hidden.
     */
    announce(timer, { days, hours, minutes }) {
      const status = timer.querySelector('[data-launch-status]');
      if (!status) return;

      const stamp = `${days}:${hours}:${minutes}`;
      if (this.announcedMinute.get(timer) === stamp) return;
      this.announcedMinute.set(timer, stamp);

      const labels = timer.dataset.launchUnitLabels || '';
      const [dayLabel = 'days', hourLabel = 'hours', minuteLabel = 'minutes'] = labels.split('|');
      const parts = [];
      if (days > 0) parts.push(`${days} ${dayLabel}`);
      if (days > 0 || hours > 0) parts.push(`${hours} ${hourLabel}`);
      parts.push(`${minutes} ${minuteLabel}`);
      status.textContent = parts.join(' ');
    }

    /*
     * El ancho del bloque se iguala al del título solo al registrar el timer
     * y al redimensionar, nunca en cada tick.
     */
    syncWidth(timer) {
      if (timer.dataset.launchRole !== 'full') return;
      const block = timer.closest('.main-product__block-scheduled_launch');
      if (!block) return;
      const title = timer
        .closest('.m-product-info--wrapper')
        ?.querySelector('.main-product__block-title .m-product-title');
      const width = Math.ceil(title?.getBoundingClientRect().width || 0);
      if (width > 0) timer.style.setProperty('--scheduled-launch-title-width', `${width}px`);
    }

    releaseTimer(timer) {
      const status = timer.querySelector('[data-launch-status]');
      if (status) status.textContent = timer.dataset.releasedText || strings().released || '';

      timer.hidden = true;
      timer.closest('.main-product__block-scheduled_launch')?.setAttribute('hidden', '');
      timer.removeAttribute('data-scheduled-launch-at');
      this.forget(timer, this.timers);
      timer.dispatchEvent(new CustomEvent('scheduled-launch:released', { bubbles: true }));
    }

    /* ------------------------------------------------------------ producto */

    /*
     * variant-picker reescribe el botón de compra al inicializarse y en cada
     * cambio de variante, así que el bloqueo se reafirma en lugar de aplicarse
     * una sola vez.
     */
    lockForm(form) {
      const locked = strings().comingSoon;

      form.querySelectorAll(SELECTORS.control).forEach((control) => {
        claim(control, 'disabled', 'disabled');
        claim(control, 'aria-disabled', 'true');
        if (control.matches('.m-add-to-cart')) claimClass(control, 'disabled');
        const label = control.querySelector('.m-add-to-cart--text');
        if (label && locked && label.textContent.trim() !== locked) label.textContent = locked;
      });
      form.querySelectorAll(SELECTORS.dynamicCheckout).forEach((checkout) => {
        claim(checkout, 'hidden', '');
        claim(checkout, 'inert', '');
        claim(checkout, 'aria-hidden', 'true');
      });
    }

    releaseForm(form) {
      const available = this.variantAvailable(form);

      form.querySelectorAll(SELECTORS.control).forEach((control) => {
        releaseAll(control, ['disabled', 'aria-disabled']);
        releaseClass(control, 'disabled');
        if (!available) {
          control.setAttribute('disabled', 'disabled');
          control.classList.add('disabled');
        }
        const label = control.querySelector('.m-add-to-cart--text');
        if (label) {
          label.textContent = available
            ? control.dataset.atcText || window.MinimogStrings?.addToCart || label.textContent
            : window.MinimogStrings?.soldOut || label.textContent;
        }
      });

      form.querySelectorAll(SELECTORS.dynamicCheckout).forEach((checkout) => {
        releaseAll(checkout, ['hidden', 'inert', 'aria-hidden']);
        checkout.classList.toggle('disabled', !available);
      });

      form.removeAttribute('data-scheduled-launch-at');
      form.removeAttribute('data-scheduled-launch-form');
      this.unwatchVariants(form);
      this.forget(form, this.forms);
    }

    variantAvailable(form) {
      const fallback = form.dataset.launchAvailable === 'true';
      const variantId = Number(form.querySelector('[name="id"]')?.value);
      if (!variantId) return fallback;

      const fromTheme = window._themeProducts?.[form.dataset.productId]?.variants;
      const known = fromTheme?.find((variant) => Number(variant.id) === variantId);
      if (known) return Boolean(known.available);

      const json = form
        .closest('.m-main-product--wrapper, [data-view="card"]')
        ?.querySelector('#productVariants[type="application/json"]');
      if (json) {
        try {
          const current = JSON.parse(json.textContent).find((variant) => Number(variant.id) === variantId);
          if (current) return Boolean(current.available);
        } catch (error) {
          /* Se conserva la disponibilidad renderizada por Liquid. */
        }
      }
      return fallback;
    }

    /*
     * variant-picker vuelve a habilitar [name="add"] al cambiar de variante y
     * emite este evento justo después. Escucharlo evita tener que observar
     * atributos en todo el documento.
     */
    watchVariants(form) {
      const productId = form.dataset.productId;
      if (!productId || !window.MinimogEvents || this.variantSubscriptions.has(form)) return;

      const unsubscribe = window.MinimogEvents.subscribe(`${productId}__VARIANT_CHANGE`, () => {
        if (isActive(form)) this.lockForm(form);
      });
      this.variantSubscriptions.set(form, unsubscribe);
    }

    unwatchVariants(form) {
      const unsubscribe = this.variantSubscriptions.get(form);
      if (typeof unsubscribe === 'function') unsubscribe();
      this.variantSubscriptions.delete(form);
    }

    /* -------------------------------------------------------------- carrito */

    cartBlocked(now = Date.now()) {
      return [...document.querySelectorAll(SELECTORS.cartTimer)].some((timer) => isActive(timer, now));
    }

    /*
     * theme-data.liquid gobierna [data-terms-action] desde el checkbox del
     * hermano anterior. Solo ocurre en el drawer: en la página de carrito el
     * atributo vive en el div envolvente, no en el botón.
     */
    termsPending(control) {
      if (!control.hasAttribute('data-terms-action')) return false;
      const terms = control.previousElementSibling?.querySelector('[name="agree_terms"]');
      /* Ante la duda no se habilita el checkout: si no se encuentra el
         checkbox, los términos se dan por pendientes. */
      return !terms || !terms.checked;
    }

    /*
     * Aceptar los términos retira el disabled del checkout desde el handler del
     * theme. Se reevalúa en microtarea, justo después de ese handler, para que
     * el botón no quede visualmente activo ni un instante durante el
     * lanzamiento, sin esperar al siguiente tick.
     */
    handleTermsChange(event) {
      if (!event.target.matches?.('[name="agree_terms"]')) return;
      Promise.resolve().then(() => this.syncCheckout());
    }

    syncCheckout() {
      const blocked = this.cartBlocked();

      document.querySelectorAll(SELECTORS.checkout).forEach((control) => {
        if (blocked) {
          /*
           * En el drawer el botón lleva a la vez data-scheduled-launch-checkout
           * y data-terms-action, y theme-data retira su disabled al aceptar los
           * términos. El lanzamiento manda mientras la fecha siga activa, así
           * que lo vuelve a reclamar en lugar de cederlo.
           */
          claim(control, 'disabled', 'disabled');
          claim(control, 'aria-disabled', 'true');
        } else {
          const wasLocked = owned.get(control)?.has('disabled');
          releaseAll(control, ['disabled', 'aria-disabled']);
          /* Al vencer, los términos recuperan el control del botón. */
          if (wasLocked && this.termsPending(control)) control.setAttribute('disabled', 'disabled');
        }
      });

      document.querySelectorAll(SELECTORS.acceleratedCheckout).forEach((checkout) => {
        if (blocked) {
          claim(checkout, 'hidden', '');
          claim(checkout, 'inert', '');
          claim(checkout, 'aria-hidden', 'true');
        } else {
          releaseAll(checkout, ['hidden', 'inert', 'aria-hidden']);
        }
      });
    }

    /*
     * Dos cosas ocurren después del arranque y ninguna emite un evento propio:
     * el quick view y las rejillas de tarjetas insertan marcado de lanzamiento,
     * y el carrito reemplaza [data-minimog-cart-items] vía Section Rendering al
     * cambiar cantidades o quitar líneas. Se observa solo childList, nunca
     * atributos, y el callback mira únicamente los nodos añadidos.
     */
    watchDom() {
      this.observer = new MutationObserver((mutations) => {
        let discovered = false;
        let cartChanged = false;

        for (const mutation of mutations) {
          if (!mutation.addedNodes.length) continue;
          if (mutation.target.matches?.(SELECTORS.cartItems)) cartChanged = true;
          if (discovered) continue;
          for (const node of mutation.addedNodes) {
            if (node.nodeType !== 1) continue;
            if (node.matches(SELECTORS.launchMarkup) || node.querySelector(SELECTORS.launchMarkup)) {
              discovered = true;
              break;
            }
          }
        }

        if (discovered || cartChanged) this.queueSync(discovered);
      });

      this.observer.observe(document.body, { childList: true, subtree: true });
    }

    queueSync(discovered) {
      this.needsScan = this.needsScan || discovered;
      if (this.queued) return;
      this.queued = true;
      window.requestAnimationFrame(() => {
        this.queued = false;
        if (this.needsScan) {
          this.needsScan = false;
          this.scan(document);
        } else {
          this.syncCheckout();
        }
      });
    }

    subscribeCartUpdates() {
      const events = window.MinimogTheme?.pubSubEvents;
      if (!window.MinimogEvents || !events) return;
      window.MinimogEvents.subscribe(events.cartUpdate, () => this.queueSync(true));
    }

    syncBundles() {
      document.querySelectorAll(SELECTORS.bundle).forEach((bundle) => {
        const button = bundle.querySelector(SELECTORS.bundleButton);
        if (!button) return;
        const blocked = [...bundle.querySelectorAll(SELECTORS.timer)].some((timer) => isActive(timer));
        if (blocked) {
          claim(button, 'disabled', 'disabled');
          claim(button, 'aria-disabled', 'true');
        } else {
          releaseAll(button, ['disabled', 'aria-disabled']);
        }
      });
    }

    /* -------------------------------------------------------------- guardas */

    handleSubmit(event) {
      const form = event.target.closest(SELECTORS.form);
      if (form && isActive(form)) {
        event.preventDefault();
        event.stopImmediatePropagation();
      }
    }

    handleClick(event) {
      const purchase = event.target.closest(SELECTORS.purchaseControl);
      const form = purchase?.closest(SELECTORS.form);
      if (form && isActive(form)) return this.block(event);

      if (event.target.closest(SELECTORS.checkout) && this.cartBlocked()) return this.block(event);
      if (event.target.closest(SELECTORS.acceleratedCheckout) && this.cartBlocked()) return this.block(event);

      const bundle = event.target.closest(SELECTORS.bundleButton)?.closest(SELECTORS.bundle);
      if (bundle && [...bundle.querySelectorAll(SELECTORS.timer)].some((timer) => isActive(timer))) {
        this.block(event);
      }
    }

    block(event) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }
  }

  const start = () => {
    if (!window.scheduledLaunchManager) window.scheduledLaunchManager = new ScheduledLaunch();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
